import { useMemo } from 'react';

import Badge from '@adsk/alloy-react-badge';
import theme from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';
import { useLocale } from '@adsk/alloy-react-locale';
import { toLocalLongDate, translate } from '../../../../helpers';
import {
  AssigneeCell,
  DueDateCell,
  LocationCell,
  RfiTypeCell,
  StatusCell,
} from '../../../../components';
import { AssigneeType } from '../../../../sdk';

const WithCellDataConvertor =
  (Component) =>
  (propName, allowToolTip = true) =>
  // eslint-disable-next-line react/display-name,react/prop-types
  ({ cellData }) =>
    allowToolTip ? (
      <OverflowTooltip>
        <Component {...{ [propName]: cellData }} />
      </OverflowTooltip>
    ) : (
      <Component {...{ [propName]: cellData }} />
    );

const renderCreateAtCell = ({ rowData }) => {
  const { createdAt } = rowData;
  return <div>{createdAt ? toLocalLongDate(createdAt) : '-'}</div>;
};

const getCellData = ({ rowData, column }) => {
  const cellData = rowData[column.dataKey];
  if (!cellData || !cellData.length) {
    return '-';
  }
  return Array.isArray(cellData) ? cellData.join(', ') : cellData;
};

const getTranslatedCellData = ({ rowData, column }) => {
  const cellData = rowData[column.dataKey];
  if (!cellData || !cellData.length) {
    return '-';
  }
  return Array.isArray(cellData) ? cellData.map(translate).join(', ') : translate(cellData);
};

const renderInnerCell = ({ cellData }) => {
  if (!cellData) {
    return '-';
  }
  return (
    <Badge color={theme.colors.charcoal100} textColor={theme.colors.charcoal900}>
      {cellData}
    </Badge>
  );
};

const renderOverFlowTooltipCellData = ({ cellData }) => (
  <OverflowTooltip>{cellData}</OverflowTooltip>
);

const getCellDataforCustomAttributes =
  (customAttribute, customAttributesOptionsMap) =>
  ({ rowData, column }) => {
    const customAttributeById = rowData.customAttributes?.reduce(
      (accumulatedCustomAttributesById, currentCustomAttribute) => ({
        [currentCustomAttribute.id]: currentCustomAttribute,
        ...accumulatedCustomAttributesById,
      }),
      {},
    );

    const customAttributeValue = customAttributeById?.[column.dataKey]?.values;
    if (!customAttributeValue || !customAttributeValue.length) {
      return '-';
    }

    let cellData;
    if (customAttribute.dataType === 'multi_select' || customAttribute.dataType === 'select') {
      cellData = customAttributeValue.map((id) => customAttributesOptionsMap[id]);
    } else {
      cellData = customAttributeValue;
    }

    return Array.isArray(cellData) ? cellData.join(', ') : cellData;
  };

const getCustomAttributesOptionsMap = (customAttributes) => {
  const mapIdToName = {};

  customAttributes
    .filter(({ dataType }) => dataType === 'select' || dataType === 'multi_select')
    .forEach((customAttribute) => {
      customAttribute.values.forEach(({ id, name }) => {
        mapIdToName[id] = name;
      });
    });

  return mapIdToName;
};

const LocationCellRenderer = (args) => <LocationCell {...args} />;
const dueDateCellRender = (args) => <DueDateCell {...args} />;
const renderRfiType = ({ rowData }) => <RfiTypeCell rfiTypeId={rowData.rfiTypeId} />;

export default function useTableColumns(customAttributes = []) {
  const { locale } = useLocale(() => undefined);
  const customAttributesOptionsMap = useMemo(
    () => getCustomAttributesOptionsMap(customAttributes),
    [customAttributes],
  );

  const locationFieldName = 'locations';
  const locationDescriptionFieldName = 'locationDescription';

  return useMemo(
    () => [
      {
        title: translate('rpPlugin.new.status'),
        key: 'status',
        dataKey: 'status',
        width: 175,
        minWidth: 175,
        position: 0,
        cellRenderer: WithCellDataConvertor(StatusCell)('status', false),
        sortable: true,
        sortField: 'status',
      },
      {
        title: translate('rpPlugin.new.id'),
        key: 'id',
        dataKey: 'customIdentifier',
        width: 90,
        minWidth: 90,
        position: 1,
        sortable: true,
        dataGetter: getCellData,
        sortField: 'customIdentifier',
        cellRenderer: renderOverFlowTooltipCellData,
      },
      {
        title: translate('rpPlugin.new.title'),
        key: 'title',
        dataKey: 'title',
        width: 170,
        minWidth: 170,
        position: 2,
        sortable: true,
        sortField: 'title',
        cellRenderer: renderOverFlowTooltipCellData,
      },
      {
        title: translate('rpPlugin.new.assignedTo'),
        key: 'assignedTo',
        dataKey: 'assignedTo',
        width: 135,
        minWidth: 135,
        position: 3,
        cellRenderer: ({ cellData }) => (
          <AssigneeCell assignees={cellData} variant={AssigneeCell.VARIANTS.TABLE_CELL} />
        ),
      },
      {
        title: translate('rpPlugin.new.dueDate'),
        key: 'dueDate',
        dataKey: 'dueDate',
        width: 125,
        minWidth: 125,
        position: 4,
        cellRenderer: dueDateCellRender,
        sortable: true,
        sortField: 'dueDate',
      },
      {
        title: translate('rpPlugin.new.rfiType'),
        key: 'rfiTypeId',
        dataKey: 'rfiTypeId',
        width: 180,
        minWidth: 180,
        position: 1,
        dataGetter: getCellData,
        cellRenderer: renderRfiType,
      },
      {
        title: translate('rpPlugin.new.location'),
        key: locationFieldName,
        dataKey: locationFieldName,
        width: 110,
        minWidth: 110,
        position: 4.5,
        sortable: false,
        sortField: locationFieldName,
        cellRenderer: LocationCellRenderer,
      },
      {
        title: translate('rpPlugin.new.locationDetails'),
        key: locationDescriptionFieldName,
        dataKey: locationDescriptionFieldName,
        width: 110,
        minWidth: 110,
        position: 5,
        sortable: true,
        sortField: 'location',
        cellRenderer: renderOverFlowTooltipCellData,
      },
      {
        title: translate('rpPlugin.new.costImpact'),
        key: 'costImpact',
        dataKey: 'costImpact',
        width: 110,
        minWidth: 110,
        position: 6,
        dataGetter: getTranslatedCellData,
        sortable: true,
        sortField: 'costImpact',
        cellRenderer: renderOverFlowTooltipCellData,
      },
      {
        title: translate('rpPlugin.new.scheduleImpact'),
        key: 'scheduleImpact',
        dataKey: 'scheduleImpact',
        width: 110,
        minWidth: 110,
        position: 7,
        dataGetter: getTranslatedCellData,
        sortable: true,
        sortField: 'scheduleImpact',
        cellRenderer: renderOverFlowTooltipCellData,
      },
      {
        title: translate('rpPlugin.new.priority'),
        key: 'priority',
        dataKey: 'priority',
        width: 110,
        minWidth: 110,
        position: 8,
        dataGetter: getTranslatedCellData,
        sortable: true,
        sortField: 'priority',
        cellRenderer: renderOverFlowTooltipCellData,
      },
      {
        title: translate('rpPlugin.new.discipline'),
        key: 'discipline',
        dataKey: 'discipline',
        width: 130,
        minWidth: 130,
        position: 9,
        sortable: true,
        dataGetter: getTranslatedCellData,
        sortField: 'discipline',
        cellRenderer: renderOverFlowTooltipCellData,
      },
      {
        title: translate('rpPlugin.new.category'),
        key: 'category',
        dataKey: 'category',
        width: 130,
        minWidth: 130,
        position: 10,
        sortable: true,
        sortField: 'category',
        dataGetter: getTranslatedCellData,
        cellRenderer: renderOverFlowTooltipCellData,
      },
      {
        title: translate('rpPlugin.new.reference'),
        key: 'reference',
        dataKey: 'reference',
        width: 120,
        minWidth: 120,
        position: 11,
        sortable: true,
        dataGetter: getCellData,
        sortField: 'reference',
        cellRenderer: renderOverFlowTooltipCellData,
      },
      {
        title: translate('rpPlugin.new.createdBy'),
        key: 'createdBy',
        dataKey: 'createdBy',
        width: 200,
        minWidth: 200,
        position: 12,
        cellRenderer: ({ cellData }) => (
          <AssigneeCell
            assignees={[{ id: cellData, type: AssigneeType.User }]}
            variant={AssigneeCell.VARIANTS.TABLE_CELL}
          />
        ),
      },
      {
        title: translate('rpPlugin.new.comments'),
        key: 'commentsCount',
        dataKey: 'commentsCount',
        width: 90,
        minWidth: 90,
        position: 13,
        cellRenderer: renderInnerCell,
      },
      {
        title: translate('rpPlugin.new.createdAt'),
        key: 'createdAt',
        dataKey: 'createdAt',
        width: 125,
        cellRenderer: renderCreateAtCell,
        minWidth: 125,
        position: 14,
        sortable: true,
        sortField: 'createdAt',
      },
      ...customAttributes.map((customAttribute) => ({
        key: customAttribute.id,
        title: customAttribute.displayName,
        dataKey: customAttribute.id,
        cellRenderer: getCellDataforCustomAttributes(customAttribute, customAttributesOptionsMap),
        minWidth: 150,
        width: 150,
        resizable: true,
      })),
    ],
    [locale, customAttributesOptionsMap, customAttributes],
  );
}
